import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  IconButton,
  TextField,
  Paper,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Autocomplete,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SyncIcon from '@mui/icons-material/Sync';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import LoginIcon from '@mui/icons-material/Login';
import { apiService } from '../services/api';
import { useNavigate } from 'react-router-dom';

type TenantRow = {
  schoolCode: string;
  schemaName: string;
  schoolName?: string | null;
  isActive: boolean;
  createdAt?: string;
  syncedAt?: string | null;
};

type OracleSchool = {
  schoolCode: string;
  schoolName?: string | null;
};

const AdminTenantsPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingOracle, setLoadingOracle] = useState(false);
  const [tenants, setTenants] = useState<TenantRow[]>([]);
  const [oracleSchools, setOracleSchools] = useState<OracleSchool[]>([]);
  const [selectedSchoolCode, setSelectedSchoolCode] = useState<string>('');
  const [creating, setCreating] = useState(false);
  const [syncingTenant, setSyncingTenant] = useState<string | null>(null);
  const [droppingTenant, setDroppingTenant] = useState<string | null>(null);
  const [enteringTenant, setEnteringTenant] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; severity: 'success' | 'warning' | 'error'; message: string }>({
    open: false,
    severity: 'success',
    message: ''
  });

  const selectedSchool = useMemo(
    () => oracleSchools.find(s => s.schoolCode === selectedSchoolCode) || null,
    [oracleSchools, selectedSchoolCode]
  );

  const existingTenantCodes = useMemo(() => new Set(tenants.map(t => t.schoolCode)), [tenants]);

  const loadTenants = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await apiService.get<{ success: boolean; tenants: TenantRow[] }>('/api/admin/tenants');
      if (resp?.success && Array.isArray(resp.tenants)) {
        setTenants(resp.tenants);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const loadOracleSchools = useCallback(async () => {
    setLoadingOracle(true);
    try {
      // Ensure we have latest tenants so "already created" checkmarks are accurate
      await loadTenants();
      const resp = await apiService.get<{ success: boolean; schools: OracleSchool[] }>('/api/admin/tenants/oracle/schools');
      if (resp?.success && Array.isArray(resp.schools)) {
        setOracleSchools(resp.schools);
      }
    } finally {
      setLoadingOracle(false);
    }
  }, []);

  useEffect(() => {
    loadTenants().catch(() => { });
  }, [loadTenants]);

  const handleCreateTenant = async () => {
    if (!selectedSchoolCode) return;
    setCreating(true);
    try {
      const resp = await apiService.post<{ success: boolean; sync?: { success: boolean; message?: string } }>(
        `/api/admin/tenants/${encodeURIComponent(selectedSchoolCode)}/create`,
        {
          schoolName: selectedSchool?.schoolName || null,
        }
      );
      await loadTenants();
      // Keep oracle list up-to-date for checkmarks/disable
      setSelectedSchoolCode('');

      if (resp?.sync && resp.sync.success === false) {
        setSnackbar({ open: true, severity: 'warning', message: `Tạo thư viện thành công, nhưng đồng bộ Oracle lỗi: ${resp.sync.message || 'Unknown error'}` });
      } else {
        setSnackbar({ open: true, severity: 'success', message: 'Tạo thư viện thành công' });
      }
    } finally {
      setCreating(false);
    }
  };

  const handleToggleActive = async (schoolCode: string, isActive: boolean) => {
    setLoading(true);
    try {
      await apiService.patch(`/api/admin/tenants/${encodeURIComponent(schoolCode)}`, { isActive });
      await loadTenants();
    } finally {
      setLoading(false);
    }
  };

  const handleSyncTenant = async (schoolCode: string, truncate: boolean = false) => {
    setSyncingTenant(schoolCode);
    try {
      const resp = await apiService.post<{ success: boolean; sync?: { success: boolean; message?: string }; syncedAt?: string }>(
        `/api/admin/tenants/${encodeURIComponent(schoolCode)}/sync`,
        { truncate }
      );
      await loadTenants();
      if (resp?.success) {
        const message = truncate
          ? (resp.sync?.message || 'Đã xóa dữ liệu cũ và đồng bộ lại thành công')
          : (resp.sync?.message || 'Đồng bộ thành công');
        setSnackbar({ open: true, severity: 'success', message });
      } else {
        setSnackbar({ open: true, severity: 'error', message: resp.sync?.message || 'Đồng bộ thất bại' });
      }
    } catch (err: any) {
      setSnackbar({ open: true, severity: 'error', message: err?.message || 'Đồng bộ thất bại' });
    } finally {
      setSyncingTenant(null);
    }
  };

  const handleDropSchemaTenant = async (schoolCode: string) => {
    const tenant = tenants.find(t => t.schoolCode === schoolCode);
    const name = tenant?.schoolName || schoolCode;
    const ok = window.confirm(
      `⚠️ XÓA THƯ VIỆN (xoá schema + toàn bộ dữ liệu) cho trường "${name}".\n\nHành động này KHÔNG thể hoàn tác. Bạn có chắc chắn không?`
    );
    if (!ok) return;

    setDroppingTenant(schoolCode);
    try {
      await apiService.post(`/api/admin/tenants/${encodeURIComponent(schoolCode)}/drop`, { confirm: true });
      await loadTenants();
      setSnackbar({ open: true, severity: 'success', message: 'Đã xoá thư viện (schema) thành công' });
    } catch (err: any) {
      setSnackbar({ open: true, severity: 'error', message: err?.message || 'Xoá thư viện thất bại' });
    } finally {
      setDroppingTenant(null);
    }
  };

  const handleEnterTenantAsSuperAdmin = async (t: TenantRow) => {
    setEnteringTenant(t.schoolCode);
    try {
      // Ensure super-admin exists in tenant schema so tenant-scoped features work (e.g., quick borrow)
      await apiService.post(`/api/admin/tenants/${encodeURIComponent(t.schoolCode)}/ensure-super-admin`, {});
      try {
        localStorage.setItem('schoolCode', t.schoolCode);
        if (t.schoolName) localStorage.setItem('schoolName', t.schoolName);
      } catch {
        // ignore
      }
      navigate('/dashboard');
    } catch (err: any) {
      setSnackbar({ open: true, severity: 'error', message: err?.message || 'Không thể vào thư viện của trường' });
    } finally {
      setEnteringTenant(null);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Quản lý thư viện theo trường
      </Typography>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Tạo thư viện mới (đồng bộ từ Oracle)
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Button variant="outlined" onClick={loadOracleSchools} disabled={loadingOracle || loading}>
            {loadingOracle ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
            Tải danh sách trường (Oracle)
          </Button>

          <Autocomplete
            disablePortal
            sx={{ minWidth: 360 }}
            options={oracleSchools}
            value={oracleSchools.find(s => s.schoolCode === selectedSchoolCode) || null}
            onChange={(_, value) => setSelectedSchoolCode(value?.schoolCode || '')}
            getOptionLabel={(opt) => (opt.schoolName ? `${opt.schoolName} (${opt.schoolCode})` : opt.schoolCode)}
            isOptionEqualToValue={(option, value) => option.schoolCode === value.schoolCode}
            getOptionDisabled={(opt) => existingTenantCodes.has(opt.schoolCode)}
            disabled={loading || loadingOracle || oracleSchools.length === 0}
            renderOption={(props, option) => {
              const exists = existingTenantCodes.has(option.schoolCode);
              return (
                <li {...props}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" noWrap title={option.schoolName ? `${option.schoolName} (${option.schoolCode})` : option.schoolCode}>
                        {option.schoolName ? `${option.schoolName} (${option.schoolCode})` : option.schoolCode}
                      </Typography>
                    </Box>
                    {exists && <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />}
                  </Box>
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Chọn trường"
                placeholder="Gõ để tìm trường..."
              />
            )}
          />

          <Button
            variant="contained"
            onClick={handleCreateTenant}
            disabled={loading || !selectedSchoolCode}
          >
            {loading ? <CircularProgress size={18} sx={{ mr: 1, color: 'inherit' }} /> : null}
            Tạo thư viện
          </Button>
        </Box>
      </Paper>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle1">Danh sách thư viện đã tạo</Typography>
        <Button variant="outlined" onClick={loadTenants} disabled={loading}>
          Làm mới
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '35%', maxWidth: 0 }}>Trường</TableCell>
              <TableCell sx={{ width: '8%' }}>SchoolCode</TableCell>
              <TableCell sx={{ width: '8%' }}>Schema</TableCell>
              <TableCell sx={{ width: '10%' }}>Đồng bộ</TableCell>
              <TableCell sx={{ width: '12%' }}>Hoạt động</TableCell>
              <TableCell sx={{ width: '27%' }}>Hành động</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tenants.map(t => (
              <TableRow key={t.schoolCode} hover>
                <TableCell sx={{ maxWidth: 0 }}>
                  <Tooltip title={t.schoolName || ''} placement="top" disableHoverListener={!t.schoolName}>
                    <Typography noWrap sx={{ maxWidth: '100%' }}>
                      {t.schoolName || '-'}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell>{t.schoolCode}</TableCell>
                <TableCell>{t.schemaName}</TableCell>
                <TableCell>{t.syncedAt ? new Date(t.syncedAt).toLocaleString() : '-'}</TableCell>
                <TableCell>
                  <Switch
                    checked={!!t.isActive}
                    onChange={(e) => handleToggleActive(t.schoolCode, e.target.checked)}
                    disabled={loading}
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                    <Tooltip title="Vào thư viện (super admin)" placement="top">
                      <span>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleEnterTenantAsSuperAdmin(t)}
                          disabled={loading || syncingTenant === t.schoolCode || droppingTenant === t.schoolCode || enteringTenant === t.schoolCode}
                        >
                          {enteringTenant === t.schoolCode ? <CircularProgress size={18} /> : <LoginIcon fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>

                    <Tooltip title="Đồng bộ" placement="top">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleSyncTenant(t.schoolCode, false)}
                          disabled={loading || syncingTenant === t.schoolCode || droppingTenant === t.schoolCode || enteringTenant === t.schoolCode}
                        >
                          {syncingTenant === t.schoolCode ? <CircularProgress size={18} /> : <SyncIcon fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>

                    <Tooltip title="Xóa dữ liệu & đồng bộ lại" placement="top">
                      <span>
                        <IconButton
                          size="small"
                          color="warning"
                          onClick={() => {
                            if (window.confirm(`Bạn có chắc muốn xóa toàn bộ dữ liệu và đồng bộ lại từ Oracle cho trường ${t.schoolName || t.schoolCode}?`)) {
                              handleSyncTenant(t.schoolCode, true);
                            }
                          }}
                          disabled={loading || syncingTenant === t.schoolCode || droppingTenant === t.schoolCode || enteringTenant === t.schoolCode}
                        >
                          <DeleteSweepIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>

                    <Tooltip title="Xóa thư viện (xoá schema)" placement="top">
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDropSchemaTenant(t.schoolCode)}
                          disabled={loading || syncingTenant === t.schoolCode || droppingTenant === t.schoolCode || enteringTenant === t.schoolCode}
                        >
                          {droppingTenant === t.schoolCode ? <CircularProgress size={18} /> : <DeleteForeverIcon fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {tenants.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  {loading ? 'Đang tải...' : 'Chưa có thư viện nào'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Backdrop open={creating} sx={{ zIndex: (theme) => theme.zIndex.modal + 1, color: '#fff' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
          <CircularProgress color="inherit" />
          <Typography variant="body2">Đang tạo thư viện và đồng bộ dữ liệu...</Typography>
        </Box>
      </Backdrop>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminTenantsPage;


